import React from "react";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiAccountOutline,
  mdiAlertCircle,
  mdiMapMarkerOutline,
  mdiNavigation,
  mdiOpenInNew,
} from "@mdi/js";
import realEstateAgentIcon from "app/images/real-estate-agent.svg";
import { formatPhoneUS } from "utils/helperFunctions/phoneFormat";
import { formatAddress } from "app/containers/Jobs/utils/format";
import { navigateToAppointment } from "app/containers/JobCalendar/utils/mapUtils";
import {
  ClientProvidedAgent,
  ClientProvidedNotes,
  hasClientProvidedAgent,
} from "./ClientProvidedInfo";
import InfoIconSlot from "./InfoIconSlot";
import PropertyDetails, { toPropertyDetails } from "./PropertyDetails";
import "./JobPopover.css";

const AGENT_VISIBILITY = {
  HIDDEN: "hidden",
  WARNING: "warning",
  VISIBLE: "visible",
};

const toContact = (person) => {
  const source = person || {};
  return {
    id: source.id || "",
    name: source.name || "",
    phone: formatPhoneUS(source.phone),
    email: source.email || "",
    role: source.role || "",
    note: source.note || "",
  };
};

const getAgentDisplayState = (report) => {
  const agentApplicable = report?.agent_applicable;
  const agentData = report?.agent;

  if (agentApplicable === false) return AGENT_VISIBILITY.HIDDEN;
  if (agentApplicable === true && !agentData) return AGENT_VISIBILITY.WARNING;
  if (agentData) return AGENT_VISIBILITY.VISIBLE;
  return AGENT_VISIBILITY.HIDDEN;
};

const openAgent = (agentId) =>
  window.open(
    `/agent-management/${agentId}/edit`,
    "_blank",
    "noopener,noreferrer"
  );

const AgentIcon = () => (
  <img
    src={realEstateAgentIcon}
    alt=""
    width={24}
    height={24}
    className="job-popover-lead-icon"
  />
);

const ContactDetails = ({ phone, email }) =>
  phone || email ? (
    <div className="job-popover-contact-details">
      {phone && <span className="job-popover-contact-detail">{phone}</span>}
      {email && <span className="job-popover-contact-detail">{email}</span>}
    </div>
  ) : null;

const SiteInfo = ({ appointment }) => (
  <div className="job-popover-site">
    <div className="job-popover-address">
      <Icon
        path={mdiMapMarkerOutline}
        size="24px"
        className="job-popover-lead-icon"
      />
      <span className="job-popover-address-text">
        {formatAddress(appointment.job?.report?.address)}
      </span>
      <Button
        variant="link"
        className="job-popover-icon-btn job-popover-nav-button"
        onClick={() => navigateToAppointment(appointment)}
        aria-label="Navigate to address"
      >
        <Icon path={mdiNavigation} size="20px" />
      </Button>
    </div>

    <div className="job-popover-indent">
      <PropertyDetails
        details={toPropertyDetails(appointment.job?.report, appointment)}
      />
    </div>
  </div>
);

// Client, with the client-provided cards nested under it.
const ClientContact = ({ job }) => {
  const client = toContact(job?.report?.client);
  const showProvidedAgent = hasClientProvidedAgent(job);
  const hasProvidedInfo = Boolean(
    job?.client_provided_notes || showProvidedAgent
  );
  if (!client.name && !hasProvidedInfo) return null;

  return (
    <div className="job-popover-contact">
      {client.name && (
        <>
          <div className="job-popover-contact-header">
            <Icon
              path={mdiAccountOutline}
              size="24px"
              className="job-popover-lead-icon"
            />
            <span className="job-popover-contact-name">{client.name}</span>
            {client.note && (
              <InfoIconSlot
                text={client.note}
                iconClassName="job-popover-contact-info-icon"
              />
            )}
          </div>
          <ContactDetails phone={client.phone} email={client.email} />
        </>
      )}

      {hasProvidedInfo && (
        <div className="job-popover-indent job-popover-client-provided">
          {job.client_provided_notes && (
            <ClientProvidedNotes note={job.client_provided_notes} />
          )}
          {showProvidedAgent && <ClientProvidedAgent job={job} />}
        </div>
      )}
    </div>
  );
};

const AgentContact = ({ report }) => {
  const displayState = getAgentDisplayState(report);

  if (displayState === AGENT_VISIBILITY.WARNING) {
    return (
      <div className="job-popover-contact-header">
        <AgentIcon />
        <span className="no-quote">
          Agent not assigned
          <Icon
            path={mdiAlertCircle}
            size="16px"
            className="job-popover-inline-icon"
          />
        </span>
      </div>
    );
  }
  if (displayState !== AGENT_VISIBILITY.VISIBLE) return null;

  const agent = toContact(report.agent);
  return (
    <div className="job-popover-contact">
      <div className="job-popover-contact-header">
        <AgentIcon />
        <span className="job-popover-contact-name">{agent.name}</span>
        {agent.id && (
          <Button
            variant="link"
            className="job-popover-icon-btn job-popover-open-link"
            onClick={() => openAgent(agent.id)}
            aria-label="Open agent in a new tab"
          >
            <Icon path={mdiOpenInNew} size="20px" />
          </Button>
        )}
        {agent.note && (
          <InfoIconSlot
            text={agent.note}
            iconClassName="job-popover-contact-info-icon"
          />
        )}
        {agent.role && (
          <span className="job-popover-contact-role">({agent.role})</span>
        )}
      </div>
      <ContactDetails phone={agent.phone} email={agent.email} />
    </div>
  );
};

// Site, client and agent sit 8px apart as one group at the top of the popover.
const JobPopoverPeople = ({ appointment }) => (
  <div className="job-popover-people">
    <SiteInfo appointment={appointment} />
    <ClientContact job={appointment.job} />
    <AgentContact report={appointment.job?.report} />
  </div>
);

export default JobPopoverPeople;
